import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, CheckCheck, Trash2, Mail, CircleCheck, CircleDashed, Link2 } from "lucide-react";
import { API } from "../lib/site";

const Stars = ({ rating }) => (
    <span className="inline-flex gap-0.5" aria-label={`Rated ${rating} out of 5`} role="img">
        {[1, 2, 3, 4, 5].map((s) => (
            <Star
                key={s}
                size={14}
                aria-hidden="true"
                className={s <= rating ? "fill-[#C2A160] text-[#C2A160]" : "text-[#C2A160]/30"}
            />
        ))}
    </span>
);

const CommunityAdmin = ({ token }) => {
    const [data, setData] = useState({ reviews: [], subscribers: [] });
    const [tab, setTab] = useState("reviews");
    const [loading, setLoading] = useState(true);
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/community`, authHeaders);
            setData(data);
        } catch {
            toast.error("Could not load reviews and subscribers.");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        load();
    }, [load]);

    const setApproved = async (review, approved) => {
        try {
            await axios.patch(`${API}/admin/reviews/${review.id}`, { approved }, authHeaders);
            setData((d) => ({
                ...d,
                reviews: d.reviews.map((r) => (r.id === review.id ? { ...r, approved } : r)),
            }));
            toast.success(approved ? "Review published." : "Review unpublished.");
        } catch {
            toast.error("Could not update the review.");
        }
    };

    const removeReview = async (review) => {
        if (!window.confirm("Delete this review permanently?")) return;
        try {
            await axios.delete(`${API}/admin/reviews/${review.id}`, authHeaders);
            setData((d) => ({ ...d, reviews: d.reviews.filter((r) => r.id !== review.id) }));
            toast.success("Review deleted.");
        } catch {
            toast.error("Could not delete the review.");
        }
    };

    const removeSubscriber = async (sub) => {
        if (!window.confirm("Remove this subscriber?")) return;
        try {
            await axios.delete(`${API}/admin/newsletter/${sub.id}`, authHeaders);
            setData((d) => ({ ...d, subscribers: d.subscribers.filter((s) => s.id !== sub.id) }));
            toast.success("Subscriber removed.");
        } catch {
            toast.error("Could not remove the subscriber.");
        }
    };

    const pending = data.reviews.filter((r) => !r.approved).length;

    return (
        <div className="mt-16 border-t border-white/10 pt-12" data-testid="community-admin">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="eyebrow-gold">Community</p>
                    <h2 className="font-display mt-2 text-2xl font-medium text-[#FDFBF7] sm:text-3xl">
                        Reviews & Subscribers
                    </h2>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        data-testid="copy-review-invite"
                        onClick={() => {
                            navigator.clipboard
                                .writeText(`${window.location.origin}/reviews`)
                                .then(() => toast.success("Review invite link copied — share it with happy partners."))
                                .catch(() => toast.error("Could not copy the link."));
                        }}
                        className="btn-gold !px-5 !py-2.5"
                    >
                        <Link2 size={15} aria-hidden="true" /> Copy Review Invite Link
                    </button>
                    <div className="flex gap-3" role="tablist" aria-label="Community tabs">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === "reviews"}
                        data-testid="community-tab-reviews"
                        onClick={() => setTab("reviews")}
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200 ${
                            tab === "reviews"
                                ? "bg-[#C2A160] text-[#0A1711]"
                                : "border border-white/15 text-[#FDFBF7]/70 hover:border-[#C2A160] hover:text-[#C2A160]"
                        }`}
                    >
                        <Star size={15} aria-hidden="true" />
                        Reviews ({data.reviews.length})
                        {pending > 0 && (
                            <span className="rounded-full bg-[#04150D] px-2 py-0.5 text-[10px] font-extrabold text-[#D9B96A]">
                                {pending} pending
                            </span>
                        )}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === "subscribers"}
                        data-testid="community-tab-subscribers"
                        onClick={() => setTab("subscribers")}
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200 ${
                            tab === "subscribers"
                                ? "bg-[#C2A160] text-[#0A1711]"
                                : "border border-white/15 text-[#FDFBF7]/70 hover:border-[#C2A160] hover:text-[#C2A160]"
                        }`}
                    >
                        <Mail size={15} aria-hidden="true" />
                        Subscribers ({data.subscribers.length})
                    </button>
                    </div>
                </div>
            </div>

            <div className="mt-8 grid gap-4">
                {loading ? (
                    <p className="py-10 text-center text-sm text-[#FDFBF7]/50">Loading…</p>
                ) : tab === "reviews" ? (
                    data.reviews.length === 0 ? (
                        <p data-testid="community-reviews-empty" className="rounded-2xl border border-white/10 bg-[#07211A]/60 py-12 text-center text-sm text-[#FDFBF7]/50">
                            No reviews yet. New reviews will appear here for approval.
                        </p>
                    ) : (
                        data.reviews.map((r) => (
                            <article
                                key={r.id}
                                data-testid={`community-review-${r.id}`}
                                className="rounded-2xl border border-white/10 bg-[#07211A] p-6"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <div className="flex flex-wrap items-center gap-3">
                                            <h3 className="font-display text-xl font-medium text-[#FDFBF7]">{r.name}</h3>
                                            <Stars rating={r.rating} />
                                            {r.approved ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-[#1A3B2B] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#D9B96A]">
                                                    <CircleCheck size={11} aria-hidden="true" /> Published
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full border border-[#C2A160]/50 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#D9B96A]">
                                                    <CircleDashed size={11} aria-hidden="true" /> Pending
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-1 text-xs text-[#FDFBF7]/50">
                                            {[r.organization, r.email].filter(Boolean).join(" · ")}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            data-testid={`review-approve-${r.id}`}
                                            onClick={() => setApproved(r, !r.approved)}
                                            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-[#FDFBF7]/80 transition-colors hover:border-[#C2A160] hover:text-[#C2A160]"
                                        >
                                            <CheckCheck size={14} aria-hidden="true" />
                                            {r.approved ? "Unpublish" : "Approve & Publish"}
                                        </button>
                                        <button
                                            type="button"
                                            data-testid={`review-delete-${r.id}`}
                                            onClick={() => removeReview(r)}
                                            aria-label="Delete review"
                                            title="Delete review"
                                            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-[#FDFBF7]/70 transition-colors hover:border-red-400 hover:text-red-400"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                                <p className="mt-4 whitespace-pre-line border-t border-white/8 pt-4 text-sm leading-relaxed text-[#FDFBF7]/75">
                                    {r.text}
                                </p>
                            </article>
                        ))
                    )
                ) : data.subscribers.length === 0 ? (
                    <p data-testid="community-subscribers-empty" className="rounded-2xl border border-white/10 bg-[#07211A]/60 py-12 text-center text-sm text-[#FDFBF7]/50">
                        No newsletter subscribers yet.
                    </p>
                ) : (
                    data.subscribers.map((s) => (
                        <div
                            key={s.id}
                            data-testid={`subscriber-${s.id}`}
                            className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-[#07211A] px-6 py-4"
                        >
                            <div>
                                <p className="text-sm font-bold text-[#FDFBF7]">{s.first_name}</p>
                                <p className="text-xs text-[#D9B96A]">{s.email}</p>
                            </div>
                            <button
                                type="button"
                                data-testid={`subscriber-delete-${s.id}`}
                                onClick={() => removeSubscriber(s)}
                                aria-label="Remove subscriber"
                                title="Remove subscriber"
                                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 text-[#FDFBF7]/70 transition-colors hover:border-red-400 hover:text-red-400"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default CommunityAdmin;
